'use client'

import { useEffect, useRef } from 'react'
import styles from '../ktechpioneers.module.scss'

const LINE_COUNT = 220

interface Line {
  x: number
  y: number
  vx: number
  vy: number
  speed: number
  lineLen: number
  weight: number
  fadeInner: number
  brightness: number
}

function createLine(W: number, H: number, cx: number, cy: number, initial: boolean): Line {
  const angle = Math.random() * Math.PI * 2
  const maxR = Math.max(W, H) * 0.65
  const startDist = maxR * (0.5 + Math.random() * 0.5)

  let x = cx + Math.cos(angle) * startDist
  let y = cy + Math.sin(angle) * startDist

  const dx = cx - x
  const dy = cy - y
  const len = Math.sqrt(dx * dx + dy * dy)
  const vx = dx / len
  const vy = dy / len

  const fadeInner = maxR * 0.15 + Math.random() * maxR * 0.1

  if (initial) {
    const progress = Math.random() * (len - fadeInner)
    x += vx * progress
    y += vy * progress
  }

  return {
    x,
    y,
    vx,
    vy,
    speed: 2.5 + Math.random() * 3.5,
    lineLen: 10 + Math.random() * 30,
    weight: 0.5 + Math.random() * 0.8,
    fadeInner,
    brightness: 0.4 + Math.random() * 0.6,
  }
}

export default function WarpBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number>(0)
  const linesRef = useRef<Line[]>([])
  const dimsRef = useRef({ W: 0, H: 0, cx: 0, cy: 0 })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resize = () => {
      const dpr = window.devicePixelRatio || 1
      const W = window.innerWidth * dpr
      const H = window.innerHeight * dpr
      canvas.width = W
      canvas.height = H
      canvas.style.width = `${window.innerWidth}px`
      canvas.style.height = `${window.innerHeight}px`
      dimsRef.current = { W, H, cx: W / 2, cy: H / 2 }
    }

    resize()
    const { W, H, cx, cy } = dimsRef.current

    if (linesRef.current.length === 0) {
      for (let i = 0; i < LINE_COUNT; i++) {
        linesRef.current.push(createLine(W, H, cx, cy, true))
      }
    }

    const handleResize = () => {
      resize()
      const { W, H, cx, cy } = dimsRef.current
      linesRef.current = []
      for (let i = 0; i < LINE_COUNT; i++) {
        linesRef.current.push(createLine(W, H, cx, cy, true))
      }
    }
    window.addEventListener('resize', handleResize)

    const animate = () => {
      const { W, H, cx, cy } = dimsRef.current
      const maxR = Math.max(W, H) * 0.65

      ctx.fillStyle = '#000'
      ctx.fillRect(0, 0, W, H)
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, maxR * 0.46)
      grad.addColorStop(0, 'rgba(0, 97, 249, 0.2)')
      grad.addColorStop(0.3, 'rgba(0, 97, 249, 0.08)')
      grad.addColorStop(0.6, 'rgba(0, 97, 249, 0.02)')
      grad.addColorStop(1, 'rgba(0, 0, 0, 0)')
      ctx.fillStyle = grad
      ctx.fillRect(0, 0, W, H)

      for (let i = 0; i < linesRef.current.length; i++) {
        const line = linesRef.current[i]

        line.x += line.vx * line.speed
        line.y += line.vy * line.speed

        const dx = cx - line.x
        const dy = cy - line.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        if (dist < line.fadeInner) {
          linesRef.current[i] = createLine(W, H, cx, cy, false)
          continue
        }

        const fadeZone = line.fadeInner + maxR * 0.15
        let alpha: number
        if (dist < fadeZone) {
          alpha = ((dist - line.fadeInner) / (fadeZone - line.fadeInner)) * line.brightness
          alpha = Math.max(0, alpha)
        } else {
          alpha = line.brightness
        }

        const tailX = line.x - line.vx * line.lineLen
        const tailY = line.y - line.vy * line.lineLen

        ctx.beginPath()
        ctx.moveTo(tailX, tailY)
        ctx.lineTo(line.x, line.y)
        ctx.strokeStyle = `rgba(220, 225, 235, ${alpha})`
        ctx.lineWidth = line.weight
        ctx.lineCap = 'round'
        ctx.stroke()
      }

      animationRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('resize', handleResize)
      cancelAnimationFrame(animationRef.current)
    }
  }, [])

  return (
    <>
      <canvas
        ref={canvasRef}
        className="pointer-events-none fixed inset-0 z-0"
        style={{ background: '#000' }}
      />
      {/* Ambient gradient blobs */}
      <div className="pointer-events-none fixed inset-0 z-[1] overflow-hidden">
        <div
          className={`absolute rounded-full ${styles.drift1}`}
          style={{
            width: '50vw',
            height: '42vh',
            top: '28%',
            left: '25%',
            background: 'radial-gradient(ellipse, rgba(0, 128, 255, 0.15) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
        <div
          className={`absolute rounded-full ${styles.drift2}`}
          style={{
            width: '45vw',
            height: '36vh',
            top: '30%',
            left: '28%',
            background: 'radial-gradient(ellipse, rgba(0, 128, 255, 0.12) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
        <div
          className={`absolute rounded-full ${styles.drift3}`}
          style={{
            width: '38vw',
            height: '34vh',
            top: '32%',
            left: '31%',
            background: 'radial-gradient(ellipse, rgba(0, 128, 255, 0.08) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
        <div
          className={`absolute rounded-full ${styles.drift4}`}
          style={{
            width: '28vw',
            height: '25vh',
            top: '30%',
            left: '18%',
            background: 'radial-gradient(ellipse, rgba(0, 180, 120, 0.15) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
        <div
          className={`absolute rounded-full ${styles.drift5}`}
          style={{
            width: '25vw',
            height: '22vh',
            bottom: '25%',
            right: '22%',
            background: 'radial-gradient(ellipse, rgba(0, 200, 140, 0.1) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
      </div>
    </>
  )
}
